import React from 'react';
import {
    getData,
    onToggleTimeUnit,
    useRangeCalendar,
} from "shared/CustomDateRange/customDateRange";
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Table as SemanticTable } from 'semantic-ui-react';
import { CategorizedCheckboxDropdown, ClearableCheckboxDropdown, Icon, Table, TimestampFilters } from 'dyl-components';

import {
    KEY_CREATED, KEY_OWNER_ID, KEY_LAST_MODIFIED,
    KEY_CONVERT, KEY_ACTIONS, KEY_ACCOUNT,
    KEY_STAGE, KEY_CAMPAIGN_SOURCE, KEY_CREATED_BY, KEY_OUTCOME, KEY_LAST_MODIFIED_BY,
    KEY_CO_OWNER,
    KEY_LOST_REASON,
    KEY_MASTER_SECONDARY,
    KEY_PRODUCT_INTEREST,
    KEY_LEAD_PRIORITY,
    KEY_LEAD_RATING,
    KEY_LEAD_SCORE
} from 'shared/constants/MODULE_COLUMNS';
import { useSelector } from 'react-redux';
import SelectAllCheckbox from 'shared/SelectAllCheckbox';

const SortActions = ({ direction = false, onToggleSort = () => { } }) => {
    return (
        <div
            onClick={onToggleSort}
            style={{ display: "inline-flex", cursor: "pointer" }}
        >
            <Icon
                name="fa-solid fa-arrow-down-long"
                style={{
                    marginRight: -7,
                    opacity: !direction ? "100%" : "50%",
                }}
            />
            <Icon
                name="fa-solid fa-arrow-up-long"
                style={{ opacity: direction ? "100%" : "50%" }}
            />
        </div>
    );
};

const LeadsHeader = ({ onFilter, isColumnHidden }) => {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { assigned_owners = [], assigned_co_owners = [] } = useSelector(state => state.contact);
    const ownedByCategories = React.useMemo(() => [
        {
            options: [
                { key: "All", value: "All", text: "All", all: true },
                { key: "Unassigned", value: "0", text: "Unassigned" },
                ...assigned_owners.map(owner => ({
                    key: owner.id,
                    value: owner.id.toString(),
                    text: owner.username,
                })).sort((a, b) => a.text.localeCompare(b.text)),
            ],
        },
    ], [assigned_owners]);
    const coOwnedByCategories = React.useMemo(() => [
        {
            options: [
                { key: "All", value: "All", text: "All", all: true },
                { key: "Unassigned", value: "0", text: "Unassigned" },
                ...assigned_co_owners.map(co_owner => ({
                    key: co_owner.id,
                    value: co_owner.id.toString(),
                    text: co_owner.username,
                })).sort((a, b) => a.text.localeCompare(b.text)),
            ],
        },
    ], [assigned_co_owners]);
    const {
        timestamp,
        setTimestamp,
        isCustomDateRangeOpen,
        setCustomDateRangeOpen,
        userAuthTimezone,
        dateRange,
        setDateRange,
    } = useRangeCalendar(params);

    const createdOrder = params.get("order_by") === "created" && params.get("order") === "asc";

    const lastModifiedOrder = params.get("order_by") === "activity" && params.get("order") === "asc";

    const onDateRangeChange = (_, { value }) => {
        setDateRange(value);
    };

    const createdOrderFilter = () => {
        const query = new URLSearchParams(params);
        const order = !createdOrder ? "asc" : "desc";
        query.set("order", order);
        query.set("order_by", "created");
        const query_string = query.toString();
        navigate(`/leads${query_string ? `?${query_string}` : ""}`);
    };

    const lastModifiedOrderFilter = () => {
        const query = new URLSearchParams(params);
        const order = !lastModifiedOrder ? "asc" : "desc";
        query.set("order", order);
        query.set("order_by", "activity");
        const query_string = query.toString();
        navigate(`/leads${query_string ? `?${query_string}` : ""}`);
    };
    const { count, leads = [] } = useSelector((state) => {
        return {
            count: state.leads.count,
            leads: state.leads.leads,
        };
    });

    return (
        <Table.Row>
            <SemanticTable.HeaderCell>
                <SelectAllCheckbox
                    count={count}
                    data={leads?.map((lead)=>{
                        return {
                            ...lead,
                            contact_id:lead?.contact?.id
                        }
                    })}
                    type={"leads"}
                    idProperty={"contact_id"}
                />
            </SemanticTable.HeaderCell>
            {!isColumnHidden(KEY_CREATED) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>
                    <span style={{ marginRight: 5 }}>Created</span>
                    <TimestampFilters
                        timestamp={timestamp}
                        onToggleTimeUnit={(timestamp) =>
                            onToggleTimeUnit(
                                timestamp,
                                setTimestamp,
                                params,
                                navigate,
                                "/leads"
                            )
                        }
                        isCustomDateRangeOpen={isCustomDateRangeOpen}
                        onOpenCustomDateRange={() => {
                            setCustomDateRangeOpen(true);
                        }}
                        onCloseCustomDateRange={() => {
                            setCustomDateRangeOpen(false);
                        }}
                        dateRange={dateRange}
                        onDateRangeChange={onDateRangeChange}
                        getData={() =>
                            getData({
                                setCustomDateRangeOpen,
                                dateRange,
                                userAuthTimezone,
                                params,
                                navigate,
                                url: "/leads",
                            })
                        }
                    />

                    <SortActions
                        direction={createdOrder}
                        onToggleSort={() => {
                            createdOrderFilter();
                        }}
                    />
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_CREATED_BY) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>Created By</SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_LAST_MODIFIED) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>
                    Last Modified
                    <SortActions
                        direction={lastModifiedOrder}
                        onToggleSort={() => {
                            lastModifiedOrderFilter();
                        }}
                    />
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_LAST_MODIFIED_BY) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>
                    Last Modified By
                </SemanticTable.HeaderCell>
            )}
            <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>Lead ID</SemanticTable.HeaderCell>

            <SemanticTable.HeaderCell className='ModulesListTable__LargeFixedWidth' width={4}>
                Contact Name
            </SemanticTable.HeaderCell>
            {!isColumnHidden(KEY_ACCOUNT) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>
                    <ClearableCheckboxDropdown
                        text='Account'
                        name='account_type'
                        options={[
                            { key: 'household', value: 'household', text: 'Household' },
                            { key: 'business', value: 'business', text: 'Business' },
                        ]}
                        onChange={(_, { value }) => { onFilter(_, { name: 'account_type', value }) }}
                        value={params.get('account_type')?.split(',')}
                        closeOnLoad={true}
                    />
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_STAGE) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>Stage</SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_OUTCOME) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>
                    <ClearableCheckboxDropdown
                        text='Outcome'
                        name='outcome'
                        options={[
                            { key: 'none', value: 'none', text: 'None' },
                            { key: 'won_opportunity', value: 'won(opportunity)', text: 'Won (Opportunity)' },
                            { key: 'won_customer', value: 'won(customer)', text: 'Won (Customer)' },
                            { key: 'lost', value: 'lost', text: 'Lost' },
                        ]}
                        onChange={(_, { value }) => { onFilter(_, { name: 'outcome', value }) }}
                        value={params.get('outcome')?.split(',')}
                        closeOnLoad
                    />
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_LOST_REASON) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>Lost Reason/Type</SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_MASTER_SECONDARY) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>Master/Secondary</SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_PRODUCT_INTEREST) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>
                    Product Interests
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_LEAD_PRIORITY) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>
                    Priority
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_LEAD_RATING) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>
                    Rating
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_LEAD_SCORE) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>
                    Score
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_OWNER_ID) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>
                    <CategorizedCheckboxDropdown
                        text="Owned By"
                        name="owner_id"
                        categories={ownedByCategories}
                        onChange={(_, { value }) => {
                            onFilter(_, { name: "owner_id", value });
                        }}
                        value={params.get("owner_id")?.split(",")}
                        closeOnLoad={true}
                    />
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_CO_OWNER) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>
                    <CategorizedCheckboxDropdown
                        text="Co-Owned By"
                        name="co_owner_id"
                        categories={coOwnedByCategories}
                        onChange={(_, { value }) => {
                            onFilter(_, { name: "co_owner_id", value });
                        }}
                        value={params.get("co_owner_id")?.split(",")}
                        closeOnLoad={true}
                    />
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_CAMPAIGN_SOURCE) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>
                    Campaign Converted
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_CONVERT) && (
                <SemanticTable.HeaderCell className='ModulesListTable__MediumFixedWidth' width={2}>
                    Convert
                </SemanticTable.HeaderCell>
            )}
            {!isColumnHidden(KEY_ACTIONS) && (
                <SemanticTable.HeaderCell className='ModulesListTable__SmallFixedWidth' width={1}>Actions</SemanticTable.HeaderCell>
            )}
        </Table.Row>
    );
}

export default LeadsHeader;
